"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
type ScrollIntent = 'pinned' | 'reading' | 'auto';
export const useScrollBehavior = () => {
  const $ = _c(12);
  const scrollContainerRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isAtTop, setIsAtTop] = useState(false);
  const [scrollIntent, setScrollIntent] = useState("pinned");
  const lastScrollTopRef = useRef(0);
  const bottomAnchorRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        setScrollIntent("pinned");
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const scrollToVisualBottom = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const maxScrollPosition = container.scrollHeight - container.clientHeight;
        container.scrollTo({
          top: -maxScrollPosition,
          behavior: "smooth"
        });
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scrollToVisualTop = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const container_0 = scrollContainerRef.current;
      if (!container_0) {
        return;
      }
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = container_0;
      const distanceFromBottom = Math.abs(scrollTop);
      const newIsAtBottom = distanceFromBottom < 50;
      const maxNegativeScroll = -(scrollHeight - clientHeight);
      const newIsAtTop = Math.abs(scrollTop - maxNegativeScroll) < 50;
      setIsAtBottom(newIsAtBottom);
      setIsAtTop(newIsAtTop);
      const scrollDelta = Math.abs(scrollTop - lastScrollTopRef.current);
      if (scrollDelta > 20 && !newIsAtBottom) {
        setScrollIntent("reading");
      } else {
        if (newIsAtBottom) {
          setScrollIntent("pinned");
        }
      }
      lastScrollTopRef.current = scrollTop;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleScroll = t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      let rafId = null;
      let observer = null;
      const setupObserver = () => {
        const container_1 = scrollContainerRef.current;
        const anchor = bottomAnchorRef.current;
        if (!container_1 || !anchor) {
          rafId = requestAnimationFrame(setupObserver);
          return;
        }
        observer = new IntersectionObserver(t5 => {
          const [entry] = t5;
          const isIntersecting = entry.isIntersecting;
          setIsAtBottom(isIntersecting);
          if (isIntersecting) {
            setScrollIntent("pinned");
          }
        }, {
          root: container_1,
          threshold: 0,
          rootMargin: "50px"
        });
        observer.observe(anchor);
      };
      setupObserver();
      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        observer?.disconnect();
      };
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      let rafId_0 = null;
      let attachedContainer = null;
      const attachListener = () => {
        const container_2 = scrollContainerRef.current;
        if (!container_2) {
          rafId_0 = requestAnimationFrame(attachListener);
          return;
        }
        attachedContainer = container_2;
        container_2.addEventListener("scroll", handleScroll);
        handleScroll();
      };
      attachListener();
      return () => {
        if (rafId_0 !== null) {
          cancelAnimationFrame(rafId_0);
        }
        if (attachedContainer) {
          attachedContainer.removeEventListener("scroll", handleScroll);
        }
      };
    };
    t6 = [handleScroll];
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  const shouldPinToBottom = scrollIntent === "pinned" && isAtBottom;
  let t7;
  if ($[7] !== isAtBottom || $[8] !== isAtTop || $[9] !== scrollIntent || $[10] !== shouldPinToBottom) {
    t7 = {
      scrollContainerRef,
      bottomAnchorRef,
      isAtBottom,
      isAtTop,
      shouldPinToBottom,
      scrollIntent,
      scrollToVisualBottom,
      scrollToVisualTop,
      handleScroll
    };
    $[7] = isAtBottom;
    $[8] = isAtTop;
    $[9] = scrollIntent;
    $[10] = shouldPinToBottom;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};